/**
 * src/services/spatial/geometryBudget.ts
 *
 * Client-side geometry hygiene helpers used before a border geometry is
 * submitted to the geofence-proposal API (and reusable anywhere a
 * Polygon/MultiPolygon needs to respect GeoLink's limits).
 *
 * Two concerns are handled here:
 *
 *   1. PRECISION — coordinates are rounded to 6 decimal places (~11 cm at
 *      the equator), the precision mandated by the geofence geometry
 *      doctrine. More precision wastes bytes and trips the admin UI's
 *      "precision normalization" warning.
 *
 *   2. SIZE CAP — a proposed feature must serialise to < 512 KB. Dense
 *      coastlines (especially imported Shapefile/KML geometry) routinely
 *      blow past that. We apply Douglas–Peucker simplification
 *      (`@turf/simplify`) with an escalating tolerance until the feature
 *      fits, so the user never has to hand-decimate vertices.
 *
 * Everything here is pure (no React/Mapbox) so it stays unit-testable.
 */

import simplify from '@turf/simplify';
import turfArea from '@turf/area';
import type { Feature, Polygon, MultiPolygon, Geometry } from 'geojson';

/** GeoLink rejects proposal features whose JSON exceeds 512 KB. */
export const MAX_FEATURE_BYTES = 512 * 1024;

/** Leave headroom for the surrounding proposal envelope (metadata, etc.). */
const TARGET_FEATURE_BYTES = Math.floor(MAX_FEATURE_BYTES * 0.95);

type PolyGeom = Polygon | MultiPolygon;

function roundTo6(n: number): number {
  // 6 dp is the doctrine precision; Number() drops trailing-zero noise.
  return Number(n.toFixed(6));
}

/** Round every coordinate of a Polygon/MultiPolygon to 6 decimal places. */
export function roundGeometryPrecision<T extends PolyGeom>(geometry: T): T {
  const roundRing = (ring: number[][]): number[][] =>
    ring.map((pos) => {
      const [lng, lat, ...rest] = pos;
      return rest.length
        ? [roundTo6(lng), roundTo6(lat), ...rest]
        : [roundTo6(lng), roundTo6(lat)];
    });

  if (geometry.type === 'Polygon') {
    return {
      ...geometry,
      coordinates: (geometry.coordinates as number[][][]).map(roundRing),
    };
  }
  // MultiPolygon
  return {
    ...geometry,
    coordinates: (geometry.coordinates as number[][][][]).map((poly) =>
      poly.map(roundRing),
    ),
  };
}

/** UTF-8 byte length of a value once JSON-serialised. */
export function jsonByteLength(value: unknown): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  }
  // Fallback for non-DOM environments (tests).
  return Buffer.byteLength(JSON.stringify(value), 'utf8');
}

export interface FitGeometryResult {
  /** The processed geometry (rounded, possibly simplified). */
  geometry: PolyGeom;
  /** Final serialised byte length of the whole feature. */
  bytes: number;
  /** True if Douglas–Peucker simplification was applied. */
  simplified: boolean;
  /** Tolerance (degrees) used, if simplified. */
  tolerance: number;
  /** True if the geometry still exceeds the cap after max simplification. */
  overCap: boolean;
}

/**
 * Ensure a feature carrying `geometry` serialises under the 512 KB cap.
 *
 * Always rounds to 6 dp first (cheap, often enough). If the surrounding
 * `feature` still serialises over the target budget, applies Turf
 * simplification with an escalating tolerance (each step ~2× the prior)
 * until it fits or the tolerance ceiling is reached.
 *
 * @param feature  The full feature that will be serialised (used to
 *                 measure realistic byte size including properties).
 * @param geometry The Polygon/MultiPolygon to fit (usually feature.geometry).
 */
export function fitGeometryToCap(
  feature: Feature<Geometry>,
  geometry: PolyGeom,
): FitGeometryResult {
  const rounded = roundGeometryPrecision(geometry);
  let candidateFeature: Feature<Geometry> = { ...feature, geometry: rounded };
  let bytes = jsonByteLength(candidateFeature);

  if (bytes <= TARGET_FEATURE_BYTES) {
    return { geometry: rounded, bytes, simplified: false, tolerance: 0, overCap: false };
  }

  // Escalating Douglas–Peucker. Start fine (~1 m) and grow until it fits.
  const tolerances = [0.00001, 0.00002, 0.00005, 0.0001, 0.0002, 0.0005, 0.001];
  let current: PolyGeom = rounded;
  let usedTolerance = 0;

  for (const tolerance of tolerances) {
    try {
      const simplified = simplify(
        { type: 'Feature', properties: {}, geometry: rounded } as Feature<PolyGeom>,
        { tolerance, highQuality: true, mutate: false },
      );
      current = roundGeometryPrecision(simplified.geometry as PolyGeom);
      usedTolerance = tolerance;
      candidateFeature = { ...feature, geometry: current };
      bytes = jsonByteLength(candidateFeature);
      if (bytes <= TARGET_FEATURE_BYTES) {
        return { geometry: current, bytes, simplified: true, tolerance, overCap: false };
      }
    } catch {
      // Turf can throw on degenerate rings; keep the last good candidate.
      break;
    }
  }

  return {
    geometry: current,
    bytes,
    simplified: true,
    tolerance: usedTolerance,
    overCap: bytes > MAX_FEATURE_BYTES,
  };
}

/**
 * Area of a Polygon/MultiPolygon in square metres (via @turf/area),
 * tolerant of malformed input (returns 0).
 */
export function geometryAreaSqMeters(
  geometry: PolyGeom | null | undefined,
): number {
  if (!geometry) return 0;
  try {
    return turfArea(geometry as PolyGeom);
  } catch {
    return 0;
  }
}

/** Human-friendly area label (m² below 1 km², else km²). */
export function formatArea(sqMeters: number): string {
  if (!Number.isFinite(sqMeters) || sqMeters <= 0) return '—';
  if (sqMeters < 1_000_000) {
    return `${Math.round(sqMeters).toLocaleString()} m²`;
  }
  const sqKm = sqMeters / 1_000_000;
  return `${sqKm.toLocaleString(undefined, { maximumFractionDigits: sqKm < 100 ? 2 : 0 })} km²`;
}
